import { Field, Formik, Form } from 'formik';
import { useRef } from 'react';
import { connect } from 'react-redux';
import styled from 'styled-components';
import { object, string, number } from 'yup';

import {
  Tooltip,
  Button,
  OutlineCircleQuestionIcon,
} from '@frontend/legacy-components';

import FormHelpers from 'components/lib2/FormHelpers';
import Input from 'components/lib2/TextInput';

import { createPaymentTerms, paymentTermsDrawer } from './data/actions';
import { getArePaymentTermsCreating } from './data/reducer';

const ButtonContainer = styled.div`
  border-top: solid 1px #d8d8d8;
  display: flex;
  justify-content: flex-end;
  margin-top: auto;
  padding: 0.75rem 1rem;

  > :not(:last-child) {
    margin-right: 10px;
  }
`;

const FieldContainer = styled.div`
  padding: 0.75rem 1.25rem;
  flex: 1;
  width: 100%;
  overflow-y: scroll;
  overflow-x: hidden;
`;

const PeriodContainer = styled.div`
  align-items: center;
  display: flex;

  > :not(:last-child) {
    padding-right: 0.5rem;
  }
`;

const StyledForm = styled(Form)`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const connector = connect(
  (state) => ({
    loading: getArePaymentTermsCreating(state),
  }),
  {
    close: paymentTermsDrawer.close,
    create: createPaymentTerms.request,
  },
);

const PaymentTermsForm = ({ loading, close, create }) => {
  const fieldContainerRef = useRef(null);

  return (
    <Formik
      initialValues={{
        label: '',
        period: 30,
      }}
      onSubmit={({ label, period }) => {
        create({ label, period });
      }}
      validationSchema={object().shape({
        label: string().required('Payment terms are required.'),
        period: number()
          .typeError('The payment period must be a positive number.')
          .integer('The payment period must be a positive number.')
          .min(0, 'The payment period must be a positive number.'),
      })}
    >
      <StyledForm>
        <FieldContainer ref={fieldContainerRef}>
          <Field name="label">
            {({ form, field }) => {
              return (
                <FormHelpers.Field required={true} label="Terms">
                  <Input
                    autoComplete="none"
                    error={form.touched[field.name] && form.errors[field.name]}
                    onChange={(e) => {
                      form.setFieldValue(field.name, e.target.value);
                    }}
                    value={field.value}
                  />
                </FormHelpers.Field>
              );
            }}
          </Field>
          <Field name="period">
            {({ form, field }) => {
              return (
                <FormHelpers.Field label="Period" required={false}>
                  <PeriodContainer>
                    <Input
                      autoComplete="none"
                      error={
                        form.touched[field.name] && form.errors[field.name]
                      }
                      onChange={(e) => {
                        form.setFieldValue(field.name, e.target.value);
                      }}
                      step="1"
                      type="number"
                      min="0"
                      value={field.value}
                    />
                    <Tooltip
                      label={
                        <p>
                          Payment will be due {field.value ? field.value : 'X'}{' '}
                          day{field.value && field.value != 1 && 's'} from
                          receipt of order.
                        </p>
                      }
                      placement="top-end"
                    >
                      <OutlineCircleQuestionIcon boxSize={3} />
                    </Tooltip>
                  </PeriodContainer>
                </FormHelpers.Field>
              );
            }}
          </Field>
        </FieldContainer>
        <ButtonContainer>
          <Button variant="secondary" onClick={close} type="button">
            Cancel
          </Button>
          <Button variant="primary" isLoading={loading} type="submit">
            Save
          </Button>
        </ButtonContainer>
      </StyledForm>
    </Formik>
  );
};

export default connector(PaymentTermsForm);
